import { Observer } from "mobx-react"
import { useState } from "react"

import stores from "@/stores"
import { isLimitedAccessScope } from "@/utils/customerAuthGate"
import { isDevAuthGateEnabled } from "@/utils/customerAuthGateDev"

/**
 * 开发环境专用：客户授权（合规）管控的模拟开关
 *
 * 超管后台环境和带 access_scope=limited 的进入链接本地都不好复现，
 * 这里提供一个只在 dev 构建里渲染的浮层开关，用于切换：
 * - 超管后台环境（模拟 window.isFromAdmin）
 * - 客户是否授权（模拟 access_scope 是否为 limited）
 *
 * 状态存在 localStorage（`pp:dev:customer-auth-gate`），刷新后保留；
 * 也可以用 URL 参数 `?_dev_from_admin=1&_dev_is_auth=0` 直接指定。
 * 生产构建里本组件不渲染，开关值恒为「不覆盖」。
 */
export default function DevCustomerAuthGateSwitch() {
  const [collapsed, setCollapsed] = useState(true)

  if (!isDevAuthGateEnabled()) {
    return null
  }

  const { userInfoSore } = stores

  return (
    <Observer>{() => {
      const { fromAdmin, isAuth } = userInfoSore.devAuthGate
      const scoped = userInfoSore.isAdminScoped
      const statusText = scoped ? "白名单模式" : "全量路由"

      return (
        <div
          style={{
            position: "fixed",
            left: 12,
            bottom: 12,
            zIndex: 2147483000,
            fontSize: 12,
            lineHeight: 1.6,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            background: "rgba(23, 23, 23, 0.92)",
            color: "#fff",
            borderRadius: 8,
            padding: collapsed ? "4px 8px" : "8px 10px",
            boxShadow: "0 2px 8px rgba(0,0,0,.3)",
            cursor: collapsed ? "pointer" : "default",
          }}
          onClick={collapsed ? () => setCollapsed(false) : undefined}
        >
          {collapsed ? (
            <span>授权管控：{statusText}</span>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 190 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <strong>客户授权管控（dev）</strong>
                <span style={{ cursor: "pointer" }} onClick={() => setCollapsed(true)}>×</span>
              </div>

              <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={fromAdmin ?? window.isFromAdmin === true}
                  onChange={e => userInfoSore.setDevAuthGate({ fromAdmin: e.target.checked })}
                />
                超管后台环境（isFromAdmin）
              </label>

              <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={isAuth ?? !isLimitedAccessScope()}
                  onChange={e => userInfoSore.setDevAuthGate({ isAuth: e.target.checked })}
                />
                客户已授权（access_scope 非 limited）
              </label>

              <div style={{ opacity: .7 }}>
                当前：{scoped
                  ? "只保留 Orders 和 Tracking page（Appearance / CSS&HTML）"
                  : "全量路由，与商家侧一致"}
              </div>

              <button
                type="button"
                style={{
                  marginTop: 2,
                  padding: "2px 6px",
                  borderRadius: 4,
                  border: "1px solid rgba(255,255,255,.35)",
                  background: "transparent",
                  color: "#fff",
                  cursor: "pointer",
                }}
                onClick={() => userInfoSore.setDevAuthGate({ fromAdmin: null, isAuth: null })}
              >
                重置为接口真实值
              </button>
            </div>
          )}
        </div>
      )
    }}</Observer>
  )
}
